import React, { useState, useRef, createElement, Component } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Shield, MessageSquare, TrendingDown, Share2, Lock, Sparkles, Download, RefreshCw, Instagram, Facebook, Twitter, MessageCircle, Activity, ScanLine } from 'lucide-react';
import { useScan } from '../hooks/useScans';
import { motion } from 'framer-motion';
export function PublicResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('id');
  const {
    scan,
    loading,
    error
  } = useScan(scanId || undefined);
  const [downloading, setDownloading] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const handleDownloadImage = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const {
        toPng
      } = await import('html-to-image');
      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#050505'
      });
      const link = document.createElement('a');
      link.download = `toxicity-result-${scanId}.png`;
      link.href = dataUrl;
      link.click();
      setDownloading(false);
    } catch (err) {
      console.error('Failed to generate image:', err);
      setDownloading(false);
    }
  };
  const handleShareToSocial = async (platform: string) => {
    const shareUrl = window.location.href;
    const text = `I just analyzed my chat on ToxicDetector! 🚩 My toxicity score: ${scan?.score || 0}%`;
    let url = '';
    try {
      switch (platform) {
        case 'twitter':
          url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
          window.open(url, '_blank', 'width=600,height=400');
          break;
        case 'facebook':
          url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
          window.open(url, '_blank', 'width=600,height=400');
          break;
        case 'whatsapp':
          url = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`;
          window.open(url, '_blank', 'width=600,height=400');
          break;
        case 'instagram':
        case 'tiktok':
          const fullText = `${text}\n\n${shareUrl}`;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(fullText);
            const platformName = platform === 'instagram' ? 'Instagram' : 'TikTok';
            alert(`✓ Link copied! Open ${platformName} and paste it in your story or post.`);
          }
          break;
      }
      setShowShareMenu(false);
    } catch (err) {
      console.error('Share error:', err);
      alert('Failed to copy link. Please copy manually: ' + shareUrl);
    }
  };
  if (!scanId) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white" data-id="element-10050">
        <header className="bg-white/5 backdrop-blur-md border-b border-white/10" data-id="element-10051">
          <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-2" data-id="element-10052">
            <Activity className="text-red-500" data-id="element-10053" />
            <h1 className="text-xl font-black tracking-tight" data-id="element-10054">ToxicDetector</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5" data-id="element-10055">
          <div className="text-center" data-id="element-10056">
            <p className="text-white/60 mb-6 font-mono" data-id="element-10057">No scan ID provided</p>
            <Link to="/" className="inline-block bg-white text-black px-8 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all" data-id="element-10058">
              Go to Home
            </Link>
          </div>
        </div>
      </div>;
  }
  if (loading) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white" data-id="element-10059">
        <header className="bg-white/5 backdrop-blur-md border-b border-white/10" data-id="element-10060">
          <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-2" data-id="element-10061">
            <Activity className="text-red-500" data-id="element-10062" />
            <h1 className="text-xl font-black tracking-tight" data-id="element-10063">ToxicDetector</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5" data-id="element-10064">
          <div className="text-center" data-id="element-10065">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent mx-auto mb-6" data-id="element-10066"></div>
            <p className="text-white/60 font-mono uppercase tracking-widest" data-id="element-10067">
              Analyzing Data...
            </p>
          </div>
        </div>
      </div>;
  }
  if (error || !scan) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white" data-id="element-10068">
        <header className="bg-white/5 backdrop-blur-md border-b border-white/10" data-id="element-10069">
          <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-2" data-id="element-10070">
            <Activity className="text-red-500" data-id="element-10071" />
            <h1 className="text-xl font-black tracking-tight" data-id="element-10072">ToxicDetector</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5" data-id="element-10073">
          <div className="text-center" data-id="element-10074">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" data-id="element-10075" />
            <p className="text-red-400 mb-6 font-mono" data-id="element-10076">
              {error || 'Results not found'}
            </p>
            <Link to="/" className="inline-block bg-white text-black px-8 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all" data-id="element-10077">
              Go to Home
            </Link>
          </div>
        </div>
      </div>;
  }
  const score = scan.score || 0;
  const riskLevel = scan.risk_level || 'low';
  const patterns = scan.patterns || [];
  const isPaid = scan.is_unlocked || false;
  const getWittyVerdict = (score: number): string => {
    if (score >= 85) return "Girl, RUN. That's manipulation 101. 🏃‍♀️";
    if (score >= 70) return 'Major red flags detected. Trust your gut. 🚩';
    if (score >= 50) return "Proceed with caution. Something's off here. ⚠️";
    if (score >= 30) return 'Not terrible, but keep your eyes open. 👀';
    return 'Looking pretty healthy! Keep it that way. ✨';
  };
  const getThemeColors = (level: string) => {
    if (level === 'high') return {
      text: 'text-red-500',
      bg: 'bg-red-500',
      glow: 'bg-red-500/20',
      border: 'border-red-500/30'
    };
    if (level === 'medium') return {
      text: 'text-amber-500',
      bg: 'bg-amber-500',
      glow: 'bg-amber-500/20',
      border: 'border-amber-500/30'
    };
    return {
      text: 'text-emerald-500',
      bg: 'bg-emerald-500',
      glow: 'bg-emerald-500/20',
      border: 'border-emerald-500/30'
    };
  };
  const theme = getThemeColors(riskLevel);
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white relative overflow-hidden" data-id="element-10078">
      {/* Background Glow */}
      <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full ${theme.glow} blur-[120px] mix-blend-screen opacity-40 pointer-events-none`} data-id="element-10079" />

      {/* Header */}
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50" data-id="element-10080">
        <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-10081">
          <div className="flex items-center justify-between" data-id="element-10082">
            <div className="flex items-center gap-2" data-id="element-10083">
              <Activity className={theme.text} data-id="element-10084" />
              <h1 className="text-xl font-black tracking-tight" data-id="element-10085">
                ToxicDetector
              </h1>
            </div>
            <Link to="/signup" className="text-white/60 text-sm font-bold hover:text-white transition-colors" data-id="element-10086">
              Try it yourself
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 w-full max-w-2xl mx-auto px-4 py-8 relative z-10" data-id="element-10087">
        {!isPaid ? <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-10088">
            {/* Teaser Card */}
            <div className="bg-white/5 backdrop-blur-xl rounded-[32px] p-8 md:p-12 border border-white/10 shadow-2xl mb-8 relative overflow-hidden text-center" data-id="element-10089">
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-10090" />

              <div className="w-20 h-20 mx-auto bg-white/10 rounded-2xl flex items-center justify-center mb-8 border border-white/10" data-id="element-10091">
                <Lock className="w-10 h-10 text-white/50" data-id="element-10092" />
              </div>

              <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-4" data-id="element-10093">
                Results Locked
              </h2>
              <p className="text-white/60 font-mono text-sm mb-10 max-w-sm mx-auto" data-id="element-10094">
                We've analyzed {scan.message_count} messages and found potential
                patterns of manipulation.
              </p>

              {/* Blurred Preview */}
              <div className="relative mb-10" data-id="element-10095">
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent z-10" data-id="element-10096" />
                <div className="filter blur-md opacity-50 select-none" data-id="element-10097">
                  <div className="text-8xl font-black text-white mb-4" data-id="element-10098">84</div>
                  <div className="h-24 bg-white/10 rounded-xl mx-auto max-w-xs mb-4" data-id="element-10099" />
                  <div className="h-24 bg-white/10 rounded-xl mx-auto max-w-xs" data-id="element-10100" />
                </div>
              </div>

              <Link to="/payment" className="block w-full bg-white text-black text-lg font-black py-5 rounded-2xl hover:bg-gray-200 transition-all shadow-lg hover:scale-[1.02] active:scale-[0.98]" data-id="element-10101">
                Unlock Full Report — $2.99
              </Link>
              <p className="text-white/40 text-xs font-mono mt-4 uppercase tracking-widest" data-id="element-10102">
                One-time payment • Instant access
              </p>
            </div>
          </motion.div> : <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-10103">
            {/* Main Result Card - Shareable */}
            <div ref={cardRef} className="bg-[#050505] rounded-[32px] p-8 md:p-12 border border-white/10 shadow-2xl mb-8 relative overflow-hidden" data-id="element-10104">
              <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full ${theme.glow} blur-[80px] mix-blend-screen opacity-50 pointer-events-none`} data-id="element-10105" />

              <div className="relative z-10" data-id="element-10106">
                {/* Header */}
                <div className="flex items-center justify-between mb-12" data-id="element-10107">
                  <div className="flex items-center gap-3" data-id="element-10108">
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center" data-id="element-10109">
                      <ScanLine className="w-5 h-5 text-white" data-id="element-10110" />
                    </div>
                    <span className="font-black text-white tracking-tight uppercase" data-id="element-10111">
                      ToxicDetector
                    </span>
                  </div>
                  <span className="text-xs font-mono text-white/50 uppercase tracking-widest" data-id="element-10112">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>

                {/* Verdict */}
                <div className="text-center mb-12" data-id="element-10113">
                  <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight leading-tight" data-id="element-10114">
                    {getWittyVerdict(score)}
                  </h2>
                </div>

                {/* Score */}
                <div className="flex flex-col items-center justify-center mb-12" data-id="element-10115">
                  <div className="relative w-48 h-48 flex items-center justify-center mb-6" data-id="element-10116">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200" data-id="element-10117">
                      <circle cx="100" cy="100" r="88" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="12" strokeLinecap="round" data-id="element-10118" />
                      <circle cx="100" cy="100" r="88" fill="none" stroke="currentColor" className={theme.text} strokeWidth="12" strokeLinecap="round" strokeDasharray={2 * Math.PI * 88} strokeDashoffset={2 * Math.PI * 88 - score / 100 * (2 * Math.PI * 88)} data-id="element-10119" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-10120">
                      <span className="text-6xl font-black text-white tracking-tighter" data-id="element-10121">
                        {score}
                      </span>
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-2 px-6 py-2 rounded-full bg-white/5 border ${theme.border} backdrop-blur-md`} data-id="element-10122">
                    <span className={`text-sm font-black ${theme.text} uppercase tracking-widest`} data-id="element-10123">
                      {riskLevel} Risk
                    </span>
                  </div>
                </div>

                {/* Patterns */}
                {patterns.length > 0 && <div className="space-y-4 mb-12" data-id="element-10124">
                    {patterns.slice(0, 3).map((pattern: any, index) => <div key={index} className="bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 flex items-start gap-4" data-id="element-10125">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0" data-id="element-10126">
                          {getPatternIcon(pattern.type)}
                        </div>
                        <div data-id="element-10127">
                          <div className="flex items-center gap-3 mb-1" data-id="element-10128">
                            <h4 className="font-bold text-white text-lg" data-id="element-10129">
                              {pattern.type}
                            </h4>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-widest border ${getPatternColorClass(pattern.severity)}`} data-id="element-10130">
                              {pattern.severity}
                            </span>
                          </div>
                          <p className="text-white/60 text-sm leading-relaxed" data-id="element-10131">
                            {pattern.description}
                          </p>
                        </div>
                      </div>)}
                  </div>}

                {/* Footer Stats */}
                <div className="flex items-center justify-between pt-6 border-t border-white/10" data-id="element-10132">
                  <div className="flex gap-8" data-id="element-10133">
                    <div data-id="element-10134">
                      <div className="text-2xl font-black text-white" data-id="element-10135">
                        {scan.message_count}
                      </div>
                      <div className="text-[10px] text-white/40 font-mono uppercase tracking-widest" data-id="element-10136">
                        Messages
                      </div>
                    </div>
                    <div data-id="element-10137">
                      <div className="text-2xl font-black text-white" data-id="element-10138">
                        {patterns.length}
                      </div>
                      <div className="text-[10px] text-white/40 font-mono uppercase tracking-widest" data-id="element-10139">
                        Patterns
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-10140">
                    <Activity className={`w-4 h-4 ${theme.text}`} data-id="element-10141" />
                    <span className="text-sm font-black text-white tracking-tight" data-id="element-10142">
                      ToxicDetector.ai
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 mb-12" data-id="element-10143">
              <button onClick={handleDownloadImage} disabled={downloading} className="w-full bg-white text-black text-lg font-black py-4 rounded-2xl hover:bg-gray-200 transition-all shadow-lg flex items-center justify-center gap-3 disabled:opacity-50" data-id="element-10144">
                {downloading ? <>
                    <RefreshCw className="animate-spin" data-id="element-10145" /> Generating...
                  </> : <>
                    <Download size={20} data-id="element-10146" /> Save Image
                  </>}
              </button>

              <button onClick={() => setShowShareMenu(!showShareMenu)} className="w-full bg-white/10 text-white text-lg font-black py-4 rounded-2xl hover:bg-white/20 transition-all border border-white/20 flex items-center justify-center gap-3" data-id="element-10147">
                <Share2 size={20} data-id="element-10148" /> Share Verdict
              </button>

              {showShareMenu && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10" data-id="element-10149">
                  <div className="grid grid-cols-2 gap-3" data-id="element-10150">
                    <button onClick={() => handleShareToSocial('instagram')} className="bg-gradient-to-br from-purple-500 to-pink-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2" data-id="element-10151">
                      <Instagram size={18} data-id="element-10152" /> Instagram
                    </button>
                    <button onClick={() => handleShareToSocial('tiktok')} className="bg-black text-white py-3 rounded-xl font-bold border border-white/20 flex items-center justify-center gap-2" data-id="element-10153">
                      <MessageCircle size={18} data-id="element-10154" /> TikTok
                    </button>
                    <button onClick={() => handleShareToSocial('twitter')} className="bg-blue-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2" data-id="element-10155">
                      <Twitter size={18} data-id="element-10156" /> Twitter
                    </button>
                    <button onClick={() => handleShareToSocial('whatsapp')} className="bg-green-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2" data-id="element-10157">
                      <MessageCircle size={18} data-id="element-10158" /> WhatsApp
                    </button>
                  </div>
                </motion.div>}

              <Link to="/upload" className="block w-full text-white/60 text-sm font-bold py-4 text-center hover:text-white transition-colors uppercase tracking-widest" data-id="element-10159">
                Scan Another Chat
              </Link>
            </div>
          </motion.div>}
      </div>
    </div>;
}
// Helper functions
function getPatternIcon(type: string): React.ReactNode {
  const iconMap: Record<string, React.ReactNode> = {
    Gaslighting: <AlertTriangle size={20} className="text-white" data-id="element-10160" />,
    Manipulation: <Shield size={20} className="text-white" data-id="element-10161" />,
    Dismissiveness: <MessageSquare size={20} className="text-white" data-id="element-10162" />,
    'Blame Shifting': <TrendingDown size={20} className="text-white" data-id="element-10163" />,
    'Emotional Abuse': <AlertTriangle size={20} className="text-white" data-id="element-10164" />
  };
  return iconMap[type] || <AlertTriangle size={20} className="text-white" data-id="element-10165" />;
}
function getPatternColorClass(severity: string): string {
  return severity === 'HIGH' ? 'text-red-400 border-red-400/30 bg-red-400/10' : severity === 'MEDIUM' ? 'text-amber-400 border-amber-400/30 bg-amber-400/10' : 'text-emerald-400 border-emerald-400/30 bg-emerald-400/10';
}